// ABOUTME: Stripped-down virtua infinite-scroll experiment built EXACTLY by
// ABOUTME: virtua's canonical Chat.stories pattern: useRef for the shift
// ABOUTME: flag (set imperatively before setItems, reset in useLayoutEffect),
// ABOUTME: overflowAnchor:"none" on the outer scroller, virtua's onScroll
// ABOUTME: with logical offset, no cooldown, threshold-based fetch trigger.
// ABOUTME: Renders weeks via aggregated counts from a single calendarOverview
// ABOUTME: query — no per-week Apollo cascade, isolating Safari scroll
// ABOUTME: behaviour from data-fetching artefacts.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@apollo/client/react"
import { useLatest } from "@shared/hooks"
import { addWeeks, format, startOfISOWeek, subWeeks } from "date-fns"
import { VList, type VListHandle } from "virtua"
import { CalendarOverviewDocument } from "@/graphql/main/gql/graphql"

// Start with enough weeks that initial scrollHeight comfortably exceeds the
// viewport and virtua's mechanics have room to work. The Chat example uses
// 100 items; we use ~24 weeks (~6 months) which similarly gives the
// virtualizer plenty of vertical content even before any prepend.
const INITIAL_WEEKS_BEFORE = 12
const INITIAL_WEEKS_AFTER = 12
const FETCH_BATCH = 8
interface DayCounts {
  workouts: number
  events: number
  menus: number
  rests: number
}

interface WeekRowProps {
  weekStart: Date
  countsByDate: Map<string, DayCounts>
}

const WeekRow = ({ weekStart, countsByDate }: WeekRowProps) => {
  const days = useMemo(() => {
    const out: { date: string; counts: DayCounts | undefined }[] = []
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart)
      d.setDate(d.getDate() + i)
      const key = format(d, "yyyy-MM-dd")
      out.push({ date: key, counts: countsByDate.get(key) })
    }
    return out
  }, [weekStart, countsByDate])

  return (
    <div className="border-b border-gray-200 px-2 py-1">
      <div className="text-sm font-semibold text-gray-700 mb-1">Week of {format(weekStart, "MMM d, yyyy")}</div>
      <div className="grid grid-cols-7 gap-1">
        {days.map(({ date, counts }) => {
          const total = counts ? counts.workouts + counts.events + counts.menus + counts.rests : 0
          const minH = 60 + total * 40
          return (
            <div
              key={date}
              className="rounded border border-gray-300 bg-white text-xs p-2"
              style={{ minHeight: minH }}
            >
              <div className="font-medium">{date.slice(8)}</div>
              {counts ? (
                <div className="text-gray-600 mt-1 space-y-0.5">
                  {counts.workouts > 0 && <div>🏃 {counts.workouts}</div>}
                  {counts.events > 0 && <div>🏆 {counts.events}</div>}
                  {counts.menus > 0 && <div>🍽 {counts.menus}</div>}
                  {counts.rests > 0 && <div>🛌 {counts.rests}</div>}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Stable item id per date — virtua's docs warn against using array index as
// key when toggling shift, because prepending changes every index.
interface WeekItem {
  id: string
  date: Date
}

const makeItem = (date: Date): WeekItem => ({
  id: format(date, "yyyy-MM-dd"),
  date,
})

const buildInitial = (): WeekItem[] => {
  const center = startOfISOWeek(new Date())
  const arr: WeekItem[] = []
  for (let i = -INITIAL_WEEKS_BEFORE; i < 0; i++) arr.push(makeItem(addWeeks(center, i)))
  arr.push(makeItem(center))
  for (let i = 1; i <= INITIAL_WEEKS_AFTER; i++) arr.push(makeItem(addWeeks(center, i)))
  return arr
}

export const CalendarTest = () => {
  const [items, setItems] = useState<WeekItem[]>(() => buildInitial())
  const ref = useRef<VListHandle>(null)

  // Imperative shift flag — set true JUST BEFORE setItems(prev => [...new, ...prev])
  // so that virtua, on the very next render, knows items appeared at the
  // start. useLayoutEffect resets it after the render commits, so the flag
  // is true for exactly one render. This is virtua's canonical pattern from
  // its Chat.stories.tsx example.
  const isPrepend = useRef(false)
  useLayoutEffect(() => {
    isPrepend.current = false
  })

  // Single overview query covering the full loaded range. Refetches when
  // the range expands; Apollo dedupes overlap.
  const startDate = items[0]?.id ?? ""
  const lastItem = items.at(-1)
  const endDate = lastItem ? format(addWeeks(lastItem.date, 1), "yyyy-MM-dd") : ""
  const { data } = useQuery(CalendarOverviewDocument, {
    variables: { request: { startDate, endDate } },
    fetchPolicy: "cache-and-network",
    skip: !startDate || !endDate,
  })

  const countsByDate = useMemo(() => {
    const m = new Map<string, DayCounts>()
    for (const d of data?.calendarOverview.days ?? []) {
      m.set(d.date, {
        workouts: d.workouts,
        events: d.events,
        menus: d.menus,
        rests: d.rests,
      })
    }
    return m
  }, [data])

  // No initial scrollToIndex — that competed with virtua's onScroll and
  // triggered an infinite prepend loop on mount (offset=0 before virtua
  // placed us at "today"). User will land at the top of the loaded range
  // (12 weeks before today) and can scroll down to today manually.

  return (
    <div className="h-screen w-full flex flex-col">
      <div className="px-4 py-2 bg-gray-100 border-b text-sm">
        <strong>CalendarTest</strong> — virtua canonical pattern (ref-based shift, overflowAnchor:none, no cooldown).{" "}
        {items.length} weeks loaded. Range: {startDate} → {endDate}
      </div>
      <ScrollerWrapper
        onTop={() => {
          isPrepend.current = true
          setItems((prev) => {
            const oldest = prev[0]?.date
            if (!oldest) return prev
            const newOnes: WeekItem[] = []
            for (let i = FETCH_BATCH; i >= 1; i--) {
              newOnes.push(makeItem(subWeeks(oldest, i)))
            }
            return [...newOnes, ...prev]
          })
        }}
        onBot={() => {
          setItems((prev) => {
            const newest = prev.at(-1)?.date
            if (!newest) return prev
            const newOnes: WeekItem[] = []
            for (let i = 1; i <= FETCH_BATCH; i++) {
              newOnes.push(makeItem(addWeeks(newest, i)))
            }
            return [...prev, ...newOnes]
          })
        }}
      >
        <VList
          ref={ref}
          style={{ flex: 1 }}
          shift={isPrepend.current}
          itemSize={500}
        >
          {items.map((it) => (
            <WeekRow
              key={it.id}
              weekStart={it.date}
              countsByDate={countsByDate}
            />
          ))}
        </VList>
      </ScrollerWrapper>
    </div>
  )
}

const ScrollerWrapper = ({
  children,
  onTop,
  onBot,
}: {
  children: React.ReactNode
  onTop: () => void
  onBot: () => void
}) => {
  const ref = useRef<HTMLDivElement>(null)
  // Read by the scroll handler below, which must not be re-attached whenever the callbacks are rebuilt.
  const onTopRef = useLatest(onTop)
  const onBotRef = useLatest(onBot)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const handler = () => {
      const { scrollTop, scrollHeight, clientHeight } = el
      if (scrollHeight <= clientHeight) return
      const distFromTop = scrollTop
      const distFromBot = scrollHeight - clientHeight - scrollTop
      if (distFromTop < 200) onTopRef.current()
      if (distFromBot < 200) onBotRef.current()
    }
    el.addEventListener("scroll", handler, { passive: true })
    return () => el.removeEventListener("scroll", handler)
    // Both refs are stable and kept fresh by useLatest — listed, never triggers.
  }, [onTopRef, onBotRef])

  return (
    <div
      ref={ref}
      style={{
        overflowY: "auto",
        flex: 1,
        overflowAnchor: "none",
        scrollBehavior: "auto",
        overscrollBehavior: "contain",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {children}
    </div>
  )
}
